import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useAppContext } from 'Context/ReactAppContext';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { Icon } from 'Components/frameworks/icon/Icon';
import { ICONS } from 'Components/utils/icon/icons';
import { CriteriaWhenReassuranceDropdown } from 'Components/widgets/criteria/criteria-when/CriteriaWhenReassuranceDropdown';
import { TripDatePicker } from 'Components/widgets/trip-date-picker/TripDatePicker';
import { dateDiff } from 'Components/widgets/trip-date-picker/TripDatePicker.utils';
import { t } from 'Src/i18n';
import type { PricesByDays, OpeningDate } from 'Store/calendar/calendar.helper';
import 'Store/calendar/calendar.helper';
import { NbNights } from 'Components/widgets/criteria/criteria-when/NbNights';
export type Props = {
  changeDepartureDate: (arg0: Date | null | undefined) => unknown;
  changeTripDuration: (arg0: number | null | undefined) => unknown;
  dateFormat: string;
  defaultDuration?: number | null | undefined;
  departureDate: (number | null | undefined) | (moment$Moment | null | undefined);
  duration: number | null | undefined;
  isSubmittable?: boolean;
  label?: string;
  navigationByYear?: boolean;
  openCalendarToDate?: (number | null | undefined) | (moment$Moment | null | undefined);
  optionsDuration?: Array<number>;
  prices?: PricesByDays;
  openingDates?: Array<OpeningDate>;
  showDurationLabel?: boolean;
  offsetCalendar?: number | null | undefined;
  seasonLabel?: string;
  theme?: string;
};

const PriceLegend = ({ seasonLabel, nbNightsProps }) => {
  const { isDesktop } = useResponsive();
  return (
    <div className="flex flex-wrap justify-center gap-10 text-14 font-bold">
      <div className="rounded-4 bg-green px-10 py-5 text-white">{t('faceting.when.bestPrice')}</div>
      {!!seasonLabel && (
        <div className="rounded-4 bg-turquoise px-10 py-5 text-white">{t(seasonLabel)}</div>
      )}
      <div className="rounded-4 bg-orange px-10 py-5 text-white">
        {t('faceting.when.lastAvailability')}
      </div>
      <div className="rounded-4 bg-transparent px-10 py-5 text-grey line-through">
        {t('faceting.when.notAvailable')}
      </div>
      {isDesktop && (
        <div className="relative z-1 ms-40">
          <CriteriaWhenReassuranceDropdown nbNightsProps={nbNightsProps} />
        </div>
      )}
    </div>
  );
};

export const FilterWhen = ({
  changeDepartureDate,
  changeTripDuration,
  departureDate,
  duration,
  isSubmittable,
  label = '',
  navigationByYear,
  openCalendarToDate,
  openingDates = [],
  optionsDuration = [],
  prices = {},
  offsetCalendar,
  seasonLabel,
  theme = 'compact',
}: Props) => {
  const { isSeller } = useAppContext();
  const from = departureDate ? moment(departureDate).toDate() : null;
  const to =
    from && duration
      ? moment(from)
          .add({
            days: duration,
          })
          .toDate()
      : null;
  const hasPrices = Object.keys(prices || {}).length > 0;
  const [hovered, setHovered] = useState<Date | null | undefined>(from);
  useEffect(() => {
    if (!departureDate) {
      setHovered(null);
    }
  }, [departureDate]);

  const onDayMouseOver = (day: Date | null | undefined) => {
    setHovered(day);
  };

  const nbNightsProps = {
    from,
    to,
    hovered,
    duration,
  };

  const onSelect = (dates: { from: Date | null | undefined; to: Date | null | undefined }) => {
    changeDepartureDate(dates.from);

    if (dates.from && dates.to) {
      const updatedDuration = dateDiff(dates.from, dates.to);
      changeTripDuration(updatedDuration);
    } else {
      changeTripDuration(null);
    }
  };

  const cls = [
    'border-b-1 border-grey -mx-10 px-5 py-10',
    'lg:border-0 md:mx-auto lg:px-10 lg:pt-20 lg:pb-0',
  ];
  return (
    <div data-testid="FilterWhen" className="mb-40 block sm:m-0">
      {
        // TODO: cleanup !!
        // eslint-disable-next-line no-nested-ternary
        label ? (
          isSubmittable ? (
            <div className="flex items-center p-20 text-start text-12 font-bold text-orange lg:p-0">
              <Icon name={ICONS.WARNING} width="2.5em" className="mr-10 flex" />
              <span>{label}</span>
            </div>
          ) : (
            <div className="text-18 font-bold lg:text-center">{label}</div>
          )
        ) : null
      }
      <div className={cls.join(' ')}>
        {hasPrices ? (
          <div className="w-full pb-10">
            <PriceLegend seasonLabel={seasonLabel} nbNightsProps={nbNightsProps} />
          </div>
        ) : null}

        <div className="max-h-none">
          <TripDatePicker
            className="pb-0"
            departureDate={departureDate}
            hasInstalment
            navigationByYear={navigationByYear}
            offsetCalendar={offsetCalendar}
            openCalendarToDate={openCalendarToDate}
            openingDates={openingDates}
            optionsDuration={optionsDuration}
            onSelect={onSelect}
            prices={prices}
            selectedDays={{
              from,
              to,
            }}
            shouldAllowSelectingSiblingOpenDays
            theme={theme}
            onDayMouseOver={onDayMouseOver}
          />
          {isSeller && <NbNights {...nbNightsProps} />}
        </div>
      </div>
    </div>
  );
};
